/**
 * Rolling tape / voice-memo waveform:
 * - New columns are "printed" near the right edge (write head)
 * - Everything scrolls left at constant speed
 * - Peaks persist (history) and do NOT collapse when you go silent
 * - Silence prints tiny dots (min height)
 */

import { useEffect, useRef } from 'react'

interface AudioWaveformProps {
  analyser: AnalyserNode | null
  height?: number
  className?: string
  scrollSpeedPxPerSec?: number
  writeHeadRatio?: number
  barWidthPx?: number
  gapPx?: number
  minBarHeightPx?: number
  color?: string
  gain?: number
  gate?: number
}

export function AudioWaveform({
  analyser,
  height = 40,
  className = '',
  scrollSpeedPxPerSec = 70,
  writeHeadRatio = 1.0,
  barWidthPx = 2,
  gapPx = 2,
  minBarHeightPx = 2,
  color = 'rgb(100, 116, 139)',
  gain = 7.0,
  gate = 0.2,
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let rafId: number | null = null
    const history: number[] = []
    let scrollPx = 0
    let lastTs = performance.now()
    let sampleIndex = 0
    let lastW = 0
    let lastH = 0
    let lastDpr = 1
    const stride = barWidthPx + gapPx
    let timeBuf: Uint8Array | null = null
    let prefilled = false

    function ensureCanvasSized() {
      const dpr = window.devicePixelRatio || 1
      const w = canvas!.clientWidth
      const h = canvas!.clientHeight

      if (w <= 0 || h <= 0) return { w: 0, h: 0, dpr }

      if (w !== lastW || h !== lastH || dpr !== lastDpr) {
        lastW = w
        lastH = h
        lastDpr = dpr
        canvas!.width = Math.floor(w * dpr)
        canvas!.height = Math.floor(h * dpr)
        ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
      }

      return { w, h, dpr }
    }

    function maxHistoryLen(writeX: number) {
      // Enough to fill from write head to left edge, plus a buffer
      return Math.ceil((writeX + stride) / stride) + 10
    }

    function amplitudeFromTimeDomain(buf: Uint8Array, seed: number) {
      const N = buf.length
      if (N === 0) return 0

      const windowSize = Math.min(N, 256)
      const maxStart = Math.max(0, N - windowSize)
      const start = maxStart > 0 ? ((seed * 37) % (maxStart + 1)) : 0

      let sumSq = 0
      let peak = 0

      for (let i = start; i < start + windowSize; i++) {
        const v = (buf[i] - 128) / 128
        const a = Math.abs(v)
        sumSq += v * v
        if (a > peak) peak = a
      }

      const rms = Math.sqrt(sumSq / windowSize)
      let amp = 0.75 * rms + 0.25 * peak
      amp = Math.min(1, amp * gain)
      amp = Math.pow(amp, 0.7)
      if (amp < gate) amp = 0

      return amp
    }

    function roundRect(
      x: number,
      y: number,
      w: number,
      h: number,
      r: number,
    ) {
      const xr = Math.min(r, w / 2)
      const yr = Math.min(r, h / 2)
      ctx!.beginPath()
      ctx!.moveTo(x + xr, y)
      ctx!.lineTo(x + w - xr, y)
      ctx!.quadraticCurveTo(x + w, y, x + w, y + yr)
      ctx!.lineTo(x + w, y + h - yr)
      ctx!.quadraticCurveTo(x + w, y + h, x + w - xr, y + h)
      ctx!.lineTo(x + xr, y + h)
      ctx!.quadraticCurveTo(x, y + h, x, y + h - yr)
      ctx!.lineTo(x, y + yr)
      ctx!.quadraticCurveTo(x, y, x + xr, y)
      ctx!.closePath()
    }

    function loop(ts: number) {
      rafId = requestAnimationFrame(loop)

      const { w, h } = ensureCanvasSized()
      if (w <= 0 || h <= 0) return

      if (analyser && (!timeBuf || timeBuf.length !== analyser.fftSize)) {
        timeBuf = new Uint8Array(analyser.fftSize)
      }

      const dt = Math.max(0, (ts - lastTs) / 1000)
      lastTs = ts

      const writeX = w * writeHeadRatio
      const maxLen = maxHistoryLen(writeX)

      // Pre-fill with silence so dots span full width immediately
      if (!prefilled) {
        prefilled = true
        const count = Math.ceil(writeX / stride)
        for (let i = 0; i < count; i++) history.push(0)
      }

      scrollPx += scrollSpeedPxPerSec * dt

      const steps = Math.floor(scrollPx / stride)
      if (steps > 0) {
        scrollPx -= steps * stride

        if (analyser && timeBuf) {
          analyser.getByteTimeDomainData(timeBuf)
        }

        for (let s = 0; s < steps; s++) {
          const amp =
            analyser && timeBuf
              ? amplitudeFromTimeDomain(timeBuf, sampleIndex)
              : 0

          sampleIndex++
          history.push(amp)
        }

        // Only trim when significantly over max to avoid constant shifting
        if (history.length > maxLen + 20) {
          history.splice(0, history.length - maxLen)
        }
      }

      ctx!.clearRect(0, 0, w, h)
      ctx!.fillStyle = color

      const centerY = h / 2
      const maxBarH = h * 0.8
      const r = Math.min(2, barWidthPx / 2)
      const fadeZone = 16

      // Draw history bars (left of and at write head)
      for (let i = 0; i < history.length; i++) {
        const age = (history.length - 1) - i
        const x = writeX - scrollPx - age * stride

        if (x + barWidthPx < 0 || x > w) continue

        // Smooth opacity fade at left and right edges
        if (x < fadeZone) {
          ctx!.globalAlpha = Math.max(0, x / fadeZone)
        } else if (x > w - fadeZone) {
          ctx!.globalAlpha = Math.max(0, (w - x) / fadeZone)
        } else {
          ctx!.globalAlpha = 1
        }

        const v = history[i]
        const barH = Math.max(minBarHeightPx, v * maxBarH)
        const y = centerY - barH / 2

        roundRect(x, y, barWidthPx, barH, r)
        ctx!.fill()
      }

      ctx!.globalAlpha = 1

    }

    rafId = requestAnimationFrame(loop)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      try {
        const { w, h } = ensureCanvasSized()
        if (w > 0 && h > 0) ctx!.clearRect(0, 0, w, h)
      } catch { /* cleanup */ }
    }
  }, [
    analyser,
    scrollSpeedPxPerSec,
    writeHeadRatio,
    barWidthPx,
    gapPx,
    minBarHeightPx,
    color,
    gain,
    gate,
  ])

  return (
    <div className={`relative ${className}`}>
      <canvas ref={canvasRef} className="block w-full" style={{ height }} />
    </div>
  )
}
